import { useEffect, useState } from 'react'
import axios from 'axios'

import { localStorageAdapter } from '@percent/utility'
import { config } from '@percent/partner-dashboard/config'

// Exported for the MSW handler
export const GEOLOCATION_URL = 'https://api.ipgeolocation.io/ipgeo'

const CACHE_KEY = 'pd_country_from_ip'
// ipgeolocation's terms allow caching a result for 24 hours at most
const CACHE_EXPIRATION_MS = 24 * 60 * 60 * 1000
const TIMEOUT = 3 * 1000

type CachedCountry = {
  value: string
  expiresAt: number
}

type GeolocationResponse = {
  country_code3?: string
}

const readCachedCountry = (): string | null => {
  try {
    const cached: CachedCountry | null = JSON.parse(localStorageAdapter.getItem(CACHE_KEY) ?? 'null')

    return cached && Date.now() < cached.expiresAt ? cached.value : null
  } catch {
    return null
  }
}

const writeCachedCountry = (value: string) => {
  const cached: CachedCountry = { value, expiresAt: Date.now() + CACHE_EXPIRATION_MS }

  localStorageAdapter.setItem(CACHE_KEY, JSON.stringify(cached))
}

// Alpha-3 code of the visitor's country, looked up from their IP. Null until known, and when the lookup fails.
export const useCountryFromIp = () => {
  const [country, setCountry] = useState<string | null>(readCachedCountry)

  useEffect(() => {
    if (country) {
      return undefined
    }

    let cancelled = false

    // Plain axios, not the app client: that one adds our auth headers.
    axios
      .get<GeolocationResponse>(GEOLOCATION_URL, {
        params: { apiKey: config.keys.geolocation || undefined },
        timeout: TIMEOUT
      })
      .then(({ data }) => {
        if (!cancelled && data.country_code3) {
          setCountry(data.country_code3)
          writeCachedCountry(data.country_code3)
        }
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
    // Looks up once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { country }
}
